import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Today,
  People,
  TrendingUp,
  Assignment,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { dashboardAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import DashboardCalendar from '../components/DashboardCalendar';
import CustomerMixPieChart from '../components/CustomerMixPieChart';
import UserViewsChart from '../components/UserViewsChart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/* ─── GetSettled Brand Colors (Option B) ─── */
const GS = {
  blue: '#FF8C42',
  blueLight: '#FFF5EF',
  blueDark: '#E06526',
  orange: '#FF8C42',
  orangeLight: '#FFF5EF',
  orangeDark: '#E06526',
  amber: '#F0A500',
  teal: '#4A7C8E',
  tealLight: '#E6F2F5',
  tealDark: '#1E3A45',
  textPrimary: '#1E3A45',
  textMuted: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
  surface: '#F9FAFB',
  white: '#ffffff',
};

const tooltipContentStyle = {
  borderRadius: 12,
  border: `1px solid ${GS.border}`,
  backgroundColor: GS.white,
  padding: '12px 16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  animation: 'tooltipFadeIn 0.2s ease-out',
};

const BarChartTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div style={tooltipContentStyle}>
        <div style={{ fontWeight: 700, color: GS.textPrimary, marginBottom: 8, fontSize: 13 }}>{label}</div>
        {payload.map((p) => (
          <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 12 }}>
            <span style={{ width: 12, height: 12, borderRadius: 4, backgroundColor: p.color }} />
            <span style={{ color: GS.textMuted }}>{p.name}:</span>
            <span style={{ fontWeight: 700, color: GS.textPrimary }}>{p.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

BarChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      color: PropTypes.string,
    })
  ),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const CARD_ICON_COLORS = [GS.blue, GS.teal, GS.amber, GS.blueDark];

/** Normalize API date to UTC calendar day YYYY-MM-DD (matches slot calendar date). */
function normalizeDateKey(raw) {
  if (raw == null || raw === '') return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Chart range for This Week / This Month / This Year (local calendar). */
function getChartDateRange(chartPeriod) {
  const now = new Date();
  if (chartPeriod === 'This Week') {
    const start = new Date(now);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  }
  if (chartPeriod === 'This Month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  }
  const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

/** Range for GET /customer/dashboard/overall-information */
function getOverviewDateRange(overviewPeriod) {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  if (overviewPeriod === 'Last 3 Months') {
    start.setMonth(start.getMonth() - 3);
  } else if (overviewPeriod === 'Last 6 Months') {
    start.setMonth(start.getMonth() - 6);
  } else {
    start.setFullYear(start.getFullYear() - 1);
  }
  start.setHours(0, 0, 0, 0);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

function extractSlotsByDayArray(res) {
  const root = res?.data?.data ?? res?.data;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.days)) return root.days;
  if (Array.isArray(root?.items)) return root.items;
  return [];
}

function formatChartLabel(ymd, chartPeriod) {
  const [y, m, day] = ymd.split('-').map(Number);
  if (!y || !m) return ymd;
  const d = new Date(Date.UTC(y, m - 1, day));
  if (chartPeriod === 'This Week') {
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
  }
  if (chartPeriod === 'This Month') {
    return String(day);
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function addSlotRowToChartMap(byDate, slotRow) {
  const ymd = normalizeDateKey(slotRow.date ?? slotRow.slotCalendarDate ?? slotRow.day);
  if (!ymd) return;
  byDate.set(ymd, {
    bookings: Number(slotRow.bookingsCount ?? slotRow.bookings ?? slotRow.bookingCount ?? 0) || 0,
    slots: Number(slotRow.slotsCount ?? slotRow.slots ?? 0) || 0,
  });
}

function mergeAnalyticsBookingIntoChartMap(byDate, bookingRow) {
  const ymd = normalizeDateKey(bookingRow.date);
  if (!ymd) return;
  const count = Number(bookingRow.count ?? bookingRow.bookings ?? 0) || 0;
  const existing = byDate.get(ymd);
  if (existing) {
    if (existing.bookings === 0 && count > 0) existing.bookings = count;
    return;
  }
  byDate.set(ymd, { bookings: count, slots: 0 });
}

function buildMergedChartRows(slotsRows, analyticsData, chartPeriod) {
  const byDate = new Map();
  for (const slotRow of slotsRows) {
    addSlotRowToChartMap(byDate, slotRow);
  }

  const byDay = analyticsData?.bookingsByDay;
  if (Array.isArray(byDay)) {
    for (const bookingRow of byDay) {
      mergeAnalyticsBookingIntoChartMap(byDate, bookingRow);
    }
  }

  const sorted = [...byDate.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  return sorted.map(([ymd, v]) => ({
    name: formatChartLabel(ymd, chartPeriod),
    bookings: v.bookings,
    slots: v.slots,
  }));
}

function mapBookingsByDayOnly(bookingsByDay, chartPeriod) {
  if (!Array.isArray(bookingsByDay)) return [];
  return bookingsByDay
    .map((b) => {
      const ymd = normalizeDateKey(b.date);
      if (!ymd) return null;
      return {
        name: formatChartLabel(ymd, chartPeriod),
        bookings: Number(b.count ?? b.bookings ?? 0) || 0,
        slots: 0,
      };
    })
    .filter(Boolean);
}

const Dashboard = () => {
  const { t } = useLanguage();
  const [chartPeriod, setChartPeriod] = useState('This Week');

  const [summaryLoading, setSummaryLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [bookingTotals, setBookingTotals] = useState(null);
  const [todayMetrics, setTodayMetrics] = useState(null);

  const [chartData, setChartData] = useState([]);
  const [overallData, setOverallData] = useState(null);
  const [activePieIndex, setActivePieIndex] = useState(-1);

  const onPieEnter = (_, index) => setActivePieIndex(index);
  const onPieLeave = () => setActivePieIndex(-1);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await dashboardAPI.getSummary();
      const data = res?.data?.data ?? res?.data;
      if (data?.bookingTotals) setBookingTotals(data.bookingTotals);
      if (data?.today) setTodayMetrics(data.today);
    } catch {
      setBookingTotals(null);
      setTodayMetrics(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setChartLoading(true);
      try {
        const { startISO, endISO } = getChartDateRange(chartPeriod);
        const periodMap = { 'This Week': 'week', 'This Month': 'month', 'This Year': 'year' };
        const period = periodMap[chartPeriod] || 'month';

        const [slotsRes, analyticsRes] = await Promise.all([
          dashboardAPI.getSlotsByDay({ startDate: startISO, endDate: endISO }),
          dashboardAPI.getAnalytics({ period, startDate: startISO, endDate: endISO }).catch(() => null),
        ]);
        if (cancelled) return;

        const slotsRows = extractSlotsByDayArray(slotsRes);
        const analyticsData = analyticsRes?.data?.data ?? analyticsRes?.data;
        let rows = buildMergedChartRows(slotsRows, analyticsData, chartPeriod);
        if (rows.length === 0 && analyticsData?.bookingsByDay?.length) {
          rows = mapBookingsByDayOnly(analyticsData.bookingsByDay, chartPeriod);
        }
        setChartData(rows);
      } catch {
        if (!cancelled) setChartData([]);
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [chartPeriod]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setOverviewLoading(true);
      try {
        const { startISO, endISO } = getOverviewDateRange('Last 6 Months');
        const res = await dashboardAPI.getOverallInformation({
          startDate: startISO,
          endDate: endISO,
        });
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data;
        setOverallData(data && typeof data === 'object' ? data : null);
      } catch {
        if (!cancelled) setOverallData(null);
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const cardConfig = useMemo(() => {
    const total = bookingTotals?.totalBookings ?? 0;
    const completed = bookingTotals?.completed ?? 0;
    const todayB = todayMetrics?.bookingsCount ?? 0;
    const todayS = todayMetrics?.slotsCount ?? 0;

    return [
      {
        label: t('pages.dashboard.card.totalBooking'),
        value: total,
        icon: <Assignment sx={{ fontSize: 24, color: 'white' }} />,
      },
      {
        label: t('pages.dashboard.card.todayBooking'),
        value: todayB,
        icon: <Today sx={{ fontSize: 24, color: 'white' }} />,
      },
      {
        label: t('pages.dashboard.card.todaySlots'),
        value: todayS,
        icon: <People sx={{ fontSize: 24, color: 'white' }} />,
      },
      {
        label: t('pages.dashboard.card.completedBookings'),
        value: completed,
        icon: <CheckCircleOutline sx={{ fontSize: 24, color: 'white' }} />,
      },
    ];
  }, [t, bookingTotals, todayMetrics]);

  /** Donut: first-time vs return customers only (API has no third series). */
  const customerPieData = useMemo(() => {
    const first = overallData?.firstTimeCustomers ?? 0;
    const ret = overallData?.returnCustomers ?? 0;
    return [
      { name: t('pages.dashboard.pie.firstTime'), value: Math.max(0, Number(first) || 0), fill: GS.blue },
      { name: t('pages.dashboard.pie.return'), value: Math.max(0, Number(ret) || 0), fill: GS.teal },
    ];
  }, [t, overallData]);

  const customerMixTotal =
    (overallData?.firstTimeCustomers ?? 0) + (overallData?.returnCustomers ?? 0);

  const firstSharePct =
    customerMixTotal > 0
      ? (((overallData?.firstTimeCustomers ?? 0) / customerMixTotal) * 100).toFixed(0)
      : null;
  const returnSharePct =
    customerMixTotal > 0
      ? (((overallData?.returnCustomers ?? 0) / customerMixTotal) * 100).toFixed(0)
      : null;

  const cardSx = {
    elevation: 0,
    sx: {
      borderRadius: 3,
      border: `1px solid ${GS.border}`,
      bgcolor: GS.white,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)' },
    },
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto', width: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <style>{`
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .recharts-wrapper:focus,
        .recharts-surface:focus,
        .recharts-sector:focus {
          outline: none !important;
        }
      `}</style>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: GS.textPrimary, letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          {t('pages.dashboard.title')}
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.5, color: GS.textMuted, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {t('pages.dashboard.subtitle')}
        </Typography>

        <Box sx={{ mt: 1, width: 48, height: 3, borderRadius: 2, bgcolor: GS.blue }} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {cardConfig.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Paper elevation={0} {...cardSx} sx={{ ...cardSx.sx, p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: GS.textMuted, mb: 0.5 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: GS.textPrimary, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {summaryLoading ? '—' : card.value.toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 2,
                    bgcolor: CARD_ICON_COLORS[i],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} {...cardSx} sx={{ ...cardSx.sx, p: { xs: 2, sm: 3 }, height: { xs: 350, sm: 420 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: GS.textPrimary, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('pages.dashboard.chart.bookingsVsSlots')}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>{t('pages.dashboard.period')}</InputLabel>
                <Select
                  value={chartPeriod}
                  label={t('pages.dashboard.period')}
                  onChange={(e) => setChartPeriod(e.target.value)}
                  sx={{ borderRadius: 2, bgcolor: GS.white, fontSize: '0.8125rem' }}
                >
                  <MenuItem value="This Week">{t('pages.dashboard.period.thisWeek')}</MenuItem>
                  <MenuItem value="This Month">{t('pages.dashboard.period.thisMonth')}</MenuItem>
                  <MenuItem value="This Year">{t('pages.dashboard.period.thisYear')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ position: 'relative', height: { xs: 260, sm: 320 } }}>
              {chartLoading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, bgcolor: 'rgba(255,255,255,0.7)' }}>
                  <CircularProgress size={36} sx={{ color: GS.blue }} />
                </Box>
              )}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GS.border} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: GS.textMuted }} stroke={GS.border} />
                  <YAxis tick={{ fontSize: 10, fill: GS.textMuted }} stroke={GS.border} allowDecimals={false} />
                  <Tooltip
                    content={<BarChartTooltip />}
                    cursor={{ fill: `${GS.blue}10`, radius: 4 }}
                    animationDuration={200}
                    animationEasing="ease-out"
                  />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="bookings" name={t('pages.dashboard.legend.bookings')} fill={GS.blue} radius={[4, 4, 0, 0]} animationDuration={800} animationEasing="ease-out" maxBarSize={32} />
                  <Bar dataKey="slots" name={t('pages.dashboard.legend.slots')} fill={GS.teal} radius={[4, 4, 0, 0]} animationDuration={800} animationEasing="ease-out" maxBarSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper elevation={0} {...cardSx} sx={{ ...cardSx.sx, p: { xs: 2, sm: 3 }, height: { xs: 'auto', lg: 420 } }}>
            <Box >
              <Typography variant="h6" sx={{ fontWeight: 700, color: GS.textPrimary, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('pages.dashboard.chart.overallInfo')}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, color: GS.textMuted }}>
              {t('pages.dashboard.chart.customersOverview')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Box sx={{ width: { xs: 150, sm: 180 }, height: { xs: 150, sm: 180 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CustomerMixPieChart
                  overviewLoading={overviewLoading}
                  customerMixTotal={customerMixTotal}
                  customerPieData={customerPieData}
                  activePieIndex={activePieIndex}
                  onPieEnter={onPieEnter}
                  onPieLeave={onPieLeave}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, gap: 2, flex: 1, minWidth: { xs: '100%', sm: 140 } }}>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: GS.blueLight, border: `1px solid ${GS.border}`, flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: GS.textPrimary, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {overviewLoading ? '—' : (overallData?.firstTimeCustomers ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: GS.blue, fontWeight: 600 }}>{t('pages.dashboard.pie.firstTime')}</Typography>
                  {firstSharePct != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <TrendingUp sx={{ fontSize: 14, color: GS.blue }} />
                      <Typography variant="caption" sx={{ color: GS.blue, fontWeight: 600 }}>{firstSharePct}%</Typography>
                    </Box>
                  )}
                </Paper>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: GS.tealLight, border: `1px solid ${GS.border}`, flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: GS.textPrimary, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {overviewLoading ? '—' : (overallData?.returnCustomers ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: GS.teal, fontWeight: 600 }}>{t('pages.dashboard.pie.return')}</Typography>
                  {returnSharePct != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <TrendingUp sx={{ fontSize: 14, color: GS.teal }} />
                      <Typography variant="caption" sx={{ color: GS.teal, fontWeight: 600 }}>{returnSharePct}%</Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mt: 3,
                pt: 3,
                borderTop: `1px solid ${GS.border}`,
              }}
            >
              {[
                { value: overallData?.completedBookings ?? 0, label: t('pages.dashboard.summary.completed') },
                { value: overallData?.cancelledBookings ?? 0, label: t('pages.dashboard.summary.cancelled') },
                { value: overallData?.totalSlots ?? 0, label: t('pages.dashboard.summary.totalSlots') },
              ].map(({ value, label }) => (
                <Paper
                  key={label}
                  elevation={0}
                  sx={{ p: 1.5, borderRadius: 2, bgcolor: GS.surface, border: `1px solid ${GS.border}`, textAlign: 'center' }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 800, color: GS.textPrimary, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                    {overviewLoading ? '—' : Number(value).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: GS.textMuted, fontWeight: 600, mt: 0.5, display: 'block' }}>{label}</Typography>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ─── User Views Analytics (Contacts & Promotions) ─── */}
      <Box sx={{ mt: { xs: 4, sm: 6 }, minWidth: 0 }}>
        <UserViewsChart defaultPeriod="week" />
      </Box>

      <Box sx={{ mt: 3, minWidth: 0, overflow: 'hidden' }}>
        <DashboardCalendar />
      </Box>
    </Box>
  );
};

export default Dashboard;
